import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotificationPermission,
  isFcmSupported,
  registerFcmToken,
  requestNotificationPermission,
  subscribeToForegroundMessages,
  unregisterFcmToken,
} from '../services/notificationService';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { AppError } from '../lib/errors';

export const NotificationsContext = createContext(null);

/**
 * Estado del permiso de notificaciones desde la perspectiva de la UI:
 *  - 'unsupported': el browser no soporta Notification API o FCM (iOS Safari fuera de PWA).
 *  - 'default': el usuario nunca decidió.
 *  - 'granted': activado.
 *  - 'denied': bloqueado a nivel browser. Solo se puede revertir desde la configuración del sitio.
 *  - 'unknown': estamos chequeando soporte todavía (primer tick).
 */
const INITIAL_STATE = {
  isSupported: null, // null = checking
  permission: 'unknown',
  currentToken: null,
  activating: false,
};

export const NotificationsProvider = ({ children }) => {
  const { profile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [state, setState] = useState(INITIAL_STATE);
  // Espejo del navigate para que el handler de foreground no se re-suscriba
  // cada vez que cambia (los hooks de react-router pueden retornar refs nuevas).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Chequeo inicial de soporte + permiso al montar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = await isFcmSupported();
      if (cancelled) return;
      const perm = supported ? getNotificationPermission() : 'unsupported';
      setState((s) => ({
        ...s,
        isSupported: supported,
        permission: supported ? perm : 'unsupported',
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hidratación del currentToken: si la página carga con permission=granted
  // (sesión previa donde el usuario ya registró este device), recuperar el
  // token de FCM para que la UI muestre "Activadas" en vez de "no registrado".
  // registerFcmToken es idempotente: getToken devuelve el mismo token y
  // arrayUnion en users.fcmTokens no duplica.
  //
  // Sin esto, después de cualquier F5 el state arrancaba con currentToken=null
  // y la card de notificaciones mostraba el botón "Registrar este dispositivo"
  // como si nunca se hubiera registrado.
  useEffect(() => {
    if (!profile?.uid) return undefined;
    if (state.isSupported !== true) return undefined;
    if (state.permission !== 'granted') return undefined;
    if (state.currentToken) return undefined; // ya hidratado
    if (state.activating) return undefined; // no pisar el flow de activate manual

    let cancelled = false;
    (async () => {
      try {
        const token = await registerFcmToken(profile.uid);
        if (cancelled) return;
        setState((s) => ({ ...s, currentToken: token }));
      } catch (err) {
        // Hidratación silenciosa: NO mostramos toast. Si el usuario clickea
        // "Registrar este dispositivo" manualmente, ahí sí se muestra el error.
        // En este path, la card va a quedar como "tenés permiso pero el device
        // no está registrado", y el botón quedará disponible para reintentar.
        console.warn('[fcm] Hidratación inicial del token falló:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    profile?.uid,
    state.isSupported,
    state.permission,
    state.currentToken,
    state.activating,
  ]);

  // Si el usuario va a la configuración del browser, cambia el permiso y vuelve
  // al tab, refrescamos el estado para que la UI lo refleje sin recargar.
  useEffect(() => {
    if (!state.isSupported) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const perm = getNotificationPermission();
      setState((s) => (s.permission === perm ? s : { ...s, permission: perm }));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [state.isSupported]);

  // Suscripción al stream de mensajes en foreground.
  // Se monta solo cuando el usuario tiene sesión + permiso granted + soporte.
  useEffect(() => {
    if (!profile?.uid) return undefined;
    if (!state.isSupported) return undefined;
    if (state.permission !== 'granted') return undefined;

    let unsubscribe = null;
    let cancelled = false;
    (async () => {
      const unsub = await subscribeToForegroundMessages((payload) => {
        const title = payload?.notification?.title || 'PetCare';
        const body =
          payload?.notification?.body ||
          'Tenés una notificación nueva. Tocá para ver detalles.';
        const petId = payload?.data?.petId;

        toastRef.current.show(`${title} — ${body}`, {
          variant: 'info',
          duration: 8000,
          onClick: petId
            ? () => navigateRef.current(`/pets/${petId}`)
            : undefined,
        });
      });
      if (cancelled) {
        unsub?.();
      } else {
        unsubscribe = unsub;
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [profile?.uid, state.isSupported, state.permission]);

  /**
   * Pide permiso al browser y registra el token FCM para este device.
   * Devuelve { ok: true, token } o { ok: false, error } (error es AppError o Error).
   * No tira — el caller renderiza el resultado en UI.
   */
  const activate = useCallback(async () => {
    if (!profile?.uid) {
      return { ok: false, error: new AppError('No estás autenticado.', 'INVALID_INPUT') };
    }
    setState((s) => ({ ...s, activating: true }));
    try {
      const perm = await requestNotificationPermission();
      if (perm !== 'granted') {
        // 'denied' o 'default' (el user cerró el diálogo).
        setState((s) => ({ ...s, permission: perm, activating: false }));
        if (perm === 'denied') {
          return {
            ok: false,
            error: new AppError(
              'Bloqueaste las notificaciones. Activalas desde la configuración del navegador.',
              'PERMISSION_DENIED'
            ),
          };
        }
        return {
          ok: false,
          error: new AppError(
            'No nos diste permiso. Tocá "Activar" e intentá de nuevo.',
            'PERMISSION_DEFAULT'
          ),
        };
      }
      // registerFcmToken ahora tira AppError con código específico en lugar
      // de retornar null silencioso. Cualquier fallo cae al catch de abajo
      // con err.code que la UI usa para mostrar copy específico.
      const token = await registerFcmToken(profile.uid);
      setState((s) => ({
        ...s,
        permission: 'granted',
        currentToken: token,
        activating: false,
      }));
      return { ok: true, token };
    } catch (err) {
      setState((s) => ({ ...s, activating: false }));
      return { ok: false, error: err };
    }
  }, [profile?.uid]);

  /**
   * Borra el token de este device de users.fcmTokens. Idempotente.
   */
  const deactivate = useCallback(async () => {
    if (!profile?.uid || !state.currentToken) {
      setState((s) => ({ ...s, currentToken: null }));
      return;
    }
    await unregisterFcmToken(profile.uid, state.currentToken);
    setState((s) => ({ ...s, currentToken: null }));
  }, [profile?.uid, state.currentToken]);

  /**
   * Cleanup explícito antes del signOut. Mantiene la misma semántica que
   * deactivate pero NO depende de profile.uid del state (porque al volver
   * a renderizar tras signOut, profile ya es null y deactivate aborta).
   */
  const unregisterCurrentDevice = useCallback(async () => {
    if (!profile?.uid || !state.currentToken) return;
    try {
      await unregisterFcmToken(profile.uid, state.currentToken);
    } catch {
      // No bloqueamos signOut por un fallo de cleanup. El worker limpia
      // tokens muertos cuando FCM los reporta como invalid.
    }
    setState((s) => ({ ...s, currentToken: null }));
  }, [profile?.uid, state.currentToken]);

  const value = {
    isSupported: state.isSupported,
    permission: state.permission,
    currentToken: state.currentToken,
    activating: state.activating,
    activate,
    deactivate,
    unregisterCurrentDevice,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
};
